import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { avatarColor, CHAT_MAX_CHARS, ChatMessage, formatClock, initialsOf } from "../meeting-room/meeting-utils";

/** Groups back-to-back messages from one sender, like Teams. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

@Component({
  selector: "app-meeting-chat",
  standalone: false,
  templateUrl: "./meeting-chat.component.html",
  styleUrls: ["./meeting-chat.component.scss"],
})
export class MeetingChatComponent implements OnChanges, AfterViewInit {
  @Input() messages: ChatMessage[] = [];
  @Input() disabled = false;
  @Output() send = new EventEmitter<string>();
  @ViewChild("list") list?: ElementRef<HTMLDivElement>;
  @ViewChild("box") box?: ElementRef<HTMLTextAreaElement>;

  draft = "";
  readonly maxChars = CHAT_MAX_CHARS;
  private stickToBottom = true;

  ngOnChanges(changes: SimpleChanges) {
    if (changes["messages"]) this.scrollToEnd(false);
  }

  ngAfterViewInit() {
    this.scrollToEnd(true);
    // On touch devices focusing would pop the on-screen keyboard every time chat opens.
    if (window.matchMedia("(hover: hover)").matches) this.box?.nativeElement.focus();
  }

  get canSend(): boolean {
    const text = this.draft.trim();
    return !this.disabled && !!text && this.draft.length <= this.maxChars;
  }

  startsGroup(index: number): boolean {
    if (index === 0) return true;
    const previous = this.messages[index - 1];
    const current = this.messages[index];
    return previous.senderId !== current.senderId || current.at - previous.at > GROUP_WINDOW_MS;
  }

  initials(message: ChatMessage): string {
    return initialsOf(message.sender);
  }

  avatar(message: ChatMessage) {
    return avatarColor(message.senderId);
  }

  clock(at: number): string {
    return formatClock(at);
  }

  trackMessage(_: number, message: ChatMessage) {
    return message.id;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  submit() {
    if (!this.canSend) return;
    this.send.emit(this.draft.trim());
    this.draft = "";
    this.stickToBottom = true;
    this.box?.nativeElement.focus();
  }

  onScroll() {
    const el = this.list?.nativeElement;
    if (!el) return;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  private scrollToEnd(force: boolean) {
    if (!force && !this.stickToBottom) return;
    setTimeout(() => {
      const el = this.list?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
